/**
 * ReviewRoundResults — Moderator review table for Phase B engine results.
 *
 * Shows all countries ranked by GDP with all key indicators + deltas.
 * Editable fields: stability, gdp, debt, inflation, war_tiredness, nuclear_level, ai_level, economic_state.
 * Non-editable (visible): treasury, military units, production.
 *
 * SPEC: Review Round Results (M8 Public Screen Manager)
 */

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

async function authFetch(url: string, opts: RequestInit = {}): Promise<Response> {
  const token = await getToken()
  return fetch(`${API_BASE}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
}

interface CountryReview {
  id: string
  sim_name: string
  gdp: number
  stability: number
  treasury: number
  inflation: number
  debt_burden: number
  nuclear_level: number
  ai_level: number
  ai_rd_progress: number
  war_tiredness: number
  mil_ground: number
  mil_naval: number
  mil_tactical_air: number
  mil_strategic_missiles: number
  mil_air_defense: number
}

interface PrevSnapshot {
  gdp: number; stability: number; treasury: number; inflation: number
  debt_burden: number; nuclear_level: number; ai_level: number; war_tiredness: number
}

const EDITABLE_FIELDS = ['stability', 'gdp', 'debt_burden', 'inflation', 'war_tiredness', 'nuclear_level', 'ai_level']

export function ReviewRoundResults({ simId }: { simId: string }) {
  const [countries, setCountries] = useState<CountryReview[]>([])
  const [prevMap, setPrevMap] = useState<Record<string, PrevSnapshot>>({})
  const [production, setProduction] = useState<Record<string, Record<string, number>>>({})
  const [oilPrice, setOilPrice] = useState<number | null>(null)
  const [prevOilPrice, setPrevOilPrice] = useState<number | null>(null)
  const [roundNum, setRoundNum] = useState(0)
  const [pending, setPending] = useState(false)
  const [edits, setEdits] = useState<Record<string, Record<string, number | string>>>({})
  const [oilEdit, setOilEdit] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [approving, setApproving] = useState(false)

  const loadData = useCallback(async () => {
    const resp = await authFetch(`/api/sim/${simId}/review-results`)
    if (!resp.ok) return
    const json = await resp.json()
    const d = json.data
    setCountries(d.countries || [])
    setPrevMap(d.prev_snapshots || {})
    setProduction(d.production || {})
    setOilPrice(d.oil_price)
    setPrevOilPrice(d.prev_oil_price ?? null)
    setRoundNum(d.round_num)
    setPending(d.pending_review)
  }, [simId])

  useEffect(() => { loadData() }, [loadData])

  // Realtime refresh when countries change
  useEffect(() => {
    const ch = supabase.channel(`review-${simId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sim_runs', filter: `id=eq.${simId}` }, () => loadData())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, loadData])

  const handleEdit = (cc: string, field: string, value: number | string) => {
    setEdits(prev => ({
      ...prev,
      [cc]: { ...(prev[cc] || {}), [field]: value },
    }))
  }

  const handleSave = async () => {
    if (Object.keys(edits).length === 0 && oilEdit == null) return
    setSaving(true)
    try {
      await authFetch(`/api/sim/${simId}/review-results/update`, {
        method: 'POST',
        body: JSON.stringify({ updates: edits, oil_price: oilEdit }),
      })
      setEdits({})
      setOilEdit(null)
      loadData()
    } catch { alert('Save failed') }
    finally { setSaving(false) }
  }

  const handleApprove = async () => {
    if (!confirm('Approve results? This will make them visible to all participants and advance to the movement window.')) return
    // Save any pending edits first
    if (Object.keys(edits).length > 0 || oilEdit != null) await handleSave()
    setApproving(true)
    try {
      const resp = await authFetch(`/api/sim/${simId}/review-results/approve`, { method: 'POST' })
      if (!resp.ok) {
        const text = await resp.text()
        alert(`Approve failed: ${text.slice(0, 100)}`)
      } else {
        loadData()
      }
    } catch { alert('Approve failed') }
    finally { setApproving(false) }
  }

  const getVal = (cc: string, field: string, original: number | string): number | string => {
    return edits[cc]?.[field] ?? original
  }

  const getDelta = (cc: string, field: string): number | null => {
    const prev = prevMap[cc]
    if (!prev) return null
    const prevVal = (prev as Record<string, number>)[field]
    if (prevVal == null) return null
    // Use edited value if available, otherwise original from countries
    const currentVal = Number(edits[cc]?.[field] ?? countries.find(c => c.id === cc)?.[field as keyof CountryReview] ?? 0)
    return currentVal - prevVal
  }

  const isChanged = (cc: string, field: string): boolean => {
    const d = getDelta(cc, field)
    return d != null && Math.abs(d) > 0.01
  }

  const hasEdits = Object.keys(edits).length > 0 || oilEdit != null

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-body-sm font-semibold text-text-primary">Review Round {roundNum} Results</h3>
          {pending && <span className="font-data text-[10px] bg-warning/15 text-warning px-2 py-0.5 rounded">PENDING REVIEW</span>}
          {!pending && <span className="font-data text-[10px] bg-success/15 text-success px-2 py-0.5 rounded">APPROVED</span>}
        </div>
        <div className="flex items-center gap-2">
          {hasEdits && (
            <button onClick={handleSave} disabled={saving}
              className="font-body text-caption font-medium bg-action/10 text-action px-3 py-1 rounded hover:bg-action/20 disabled:opacity-40 transition-colors">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
          {pending && (
            <button onClick={handleApprove} disabled={approving}
              className="font-body text-caption font-medium bg-success/10 text-success px-3 py-1 rounded hover:bg-success/20 disabled:opacity-40 transition-colors">
              {approving ? 'Approving...' : 'Approve & Publish Results'}
            </button>
          )}
        </div>
      </div>

      {/* Oil price */}
      <div className="flex items-center gap-2 font-data text-caption">
        <span className="text-text-secondary">Oil Price:</span>
        <input type="number" step="0.1"
          value={oilEdit ?? oilPrice ?? 0}
          onChange={e => setOilEdit(Number(e.target.value))}
          className="w-16 bg-card border border-border rounded px-1.5 py-0.5 text-text-primary text-center focus:outline-none focus:border-action" />
        <span className="text-text-secondary/50">$/bbl</span>
        <DeltaSpan d={prevOilPrice != null ? (oilEdit ?? oilPrice ?? 0) - prevOilPrice : null} />
      </div>

      {/* Results table */}
      <div className="overflow-x-auto border border-border rounded">
        <table className="w-full border-collapse font-data text-caption">
          <thead>
            <tr className="bg-card/50">
              <th className="text-left px-2 py-1.5 font-medium text-text-secondary border-b border-border">#</th>
              <th className="text-left px-2 py-1.5 font-medium text-text-secondary border-b border-border">Country</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">GDP</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">%</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">Stab</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">Tres</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">Infl</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">Debt</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">War T</th>
              <th className="text-center px-2 py-1.5 font-medium text-text-secondary border-b border-border">Nuke</th>
              <th className="text-center px-2 py-1.5 font-medium text-text-secondary border-b border-border">AI</th>
              <th className="text-right px-2 py-1.5 font-medium text-text-secondary border-b border-border">Produced</th>
            </tr>
          </thead>
          <tbody>
            {countries.filter(c => c.id !== '_note').map((c, i) => {
              const prod = production[c.id] || {}
              const prodStr = Object.entries(prod).map(([t, n]) => `${t[0].toUpperCase()}${n}`).join(' ') || '—'

              return (
                <tr key={c.id} className={`border-b border-border/30 ${i < 2 ? 'bg-card/30' : ''}`}>
                  <td className="px-2 py-1 text-text-secondary/50">{i + 1}</td>
                  <td className="px-2 py-1 font-medium text-text-primary">{c.sim_name}</td>

                  {/* GDP — editable */}
                  <td className="px-1 py-1 text-right">
                    <EditableNum cc={c.id} field="gdp" value={Number(getVal(c.id, 'gdp', c.gdp))}
                      delta={getDelta(c.id, 'gdp')} onChange={handleEdit} step={0.1} decimals={1} />
                  </td>
                  {/* GDP growth % — auto-calculated */}
                  <td className="px-1 py-1 text-right text-[10px]">
                    {(() => {
                      const prev = prevMap[c.id]?.gdp
                      if (!prev || prev === 0) return <span className="text-text-secondary/30">—</span>
                      const curr = Number(getVal(c.id, 'gdp', c.gdp))
                      const pct = ((curr - prev) / prev * 100)
                      return <span className={pct >= 0 ? 'text-success' : 'text-danger'}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
                    })()}
                  </td>
                  {/* Stability — editable */}
                  <td className="px-1 py-1 text-right">
                    <EditableNum cc={c.id} field="stability" value={Number(getVal(c.id, 'stability', c.stability))}
                      delta={getDelta(c.id, 'stability')} onChange={handleEdit} step={0.01} decimals={2} />
                  </td>
                  {/* Treasury — read-only */}
                  <td className="px-2 py-1 text-right text-text-secondary">
                    <div className="flex items-center justify-end">
                      <span>ç{Number(c.treasury).toFixed(1)}</span>
                      <DeltaSpan d={getDelta(c.id, 'treasury')} />
                    </div>
                  </td>
                  {/* Inflation — editable */}
                  <td className="px-1 py-1 text-right">
                    <EditableNum cc={c.id} field="inflation" value={Number(getVal(c.id, 'inflation', c.inflation))}
                      delta={getDelta(c.id, 'inflation')} onChange={handleEdit} step={0.1} decimals={1} suffix="%" />
                  </td>
                  {/* Debt — editable */}
                  <td className="px-1 py-1 text-right">
                    <EditableNum cc={c.id} field="debt_burden" value={Number(getVal(c.id, 'debt_burden', c.debt_burden)) * 100}
                      delta={(() => { const d = getDelta(c.id, 'debt_burden'); return d != null ? d * 100 : null })()}
                      onChange={(cc, field, val) => handleEdit(cc, field, val / 100)}
                      step={1} decimals={0} suffix="%" />
                  </td>
                  {/* War tiredness — editable */}
                  <td className="px-1 py-1 text-right">
                    <EditableNum cc={c.id} field="war_tiredness" value={Number(getVal(c.id, 'war_tiredness', c.war_tiredness))}
                      delta={getDelta(c.id, 'war_tiredness')} onChange={handleEdit} step={0.1} decimals={1} />
                  </td>
                  {/* Nuclear level — editable, highlight changes */}
                  <td className={`px-1 py-1 text-center ${isChanged(c.id, 'nuclear_level') ? 'bg-danger/15' : ''}`}>
                    <EditableNum cc={c.id} field="nuclear_level" value={Number(getVal(c.id, 'nuclear_level', c.nuclear_level))}
                      delta={getDelta(c.id, 'nuclear_level')} onChange={handleEdit} step={1} decimals={0} />
                  </td>
                  {/* AI level — editable, highlight changes */}
                  <td className={`px-1 py-1 text-center ${isChanged(c.id, 'ai_level') ? 'bg-action/15' : ''}`}>
                    <EditableNum cc={c.id} field="ai_level" value={Number(getVal(c.id, 'ai_level', c.ai_level))}
                      delta={getDelta(c.id, 'ai_level')} onChange={handleEdit} step={1} decimals={0} />
                  </td>
                  {/* Production — read-only */}
                  <td className="px-2 py-1 text-right text-text-secondary/60 text-[10px]">{prodStr}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* Editable number cell — fixed width number + fixed width delta */
function EditableNum({ cc, field, value, delta, onChange, step, decimals = 1, suffix = '', width = 'w-14' }: {
  cc: string; field: string; value: number; delta: number | null
  onChange: (cc: string, field: string, val: number) => void; step: number
  decimals?: number; suffix?: string; width?: string
}) {
  return (
    <div className="flex items-center justify-end">
      <input type="number" step={step}
        value={value.toFixed(decimals)}
        onChange={e => onChange(cc, field, Number(e.target.value))}
        className={`${width} bg-transparent border-b border-border/30 hover:border-action/50 focus:border-action px-0.5 py-0 text-right text-text-primary focus:outline-none text-caption`} />
      {suffix && <span className="text-text-secondary/50 text-[9px] ml-0.5">{suffix}</span>}
      <DeltaSpan d={delta} />
    </div>
  )
}

/* Delta indicator — fixed 30px width so columns stay aligned */
function DeltaSpan({ d }: { d: number | null }) {
  if (d == null || Math.abs(d) < 0.01) return <span className="inline-block w-[30px]" />
  const isUp = d > 0
  const display = Math.abs(d) < 10 && Math.abs(d) !== Math.floor(Math.abs(d))
    ? Math.abs(d).toFixed(1) : Math.round(Math.abs(d)).toString()
  return (
    <span className={`inline-block w-[30px] text-center text-[9px] font-semibold ${isUp ? 'text-success' : 'text-danger'}`}>
      {isUp ? '▲' : '▼'}{display}
    </span>
  )
}
